import React, { FC, useMemo } from 'react';
import { Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { EPRMCode } from '../../../../services/individualReport';
import { THEME as theme } from '../../../../styles/theme';
import PageCount from '../../components/PageCount/PageCount';
import PageSection from '../../components/PageSection/PageSection';
import SeekIcon from './../../../../assets/icons/hand-holding-heart-solid.png';

const styles = StyleSheet.create({
  page: {
    position: 'relative',
    fontFamily: 'Roboto-Regular',
    backgroundColor: theme.colors.neutral.secondary,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  card: {
    display: 'flex',
    padding: `${theme.layout.spacing.deka} ${theme.layout.spacing.centy} ${theme.layout.spacing.decy}`,
    backgroundColor: `${theme.colors.neutral.primary[5]}`,
    borderRadius: 8,
  },
  inner: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    padding: `${theme.layout.spacing.deka} ${theme.layout.spacing.deka} ${theme.layout.spacing.centy}`,
    flexDirection: 'column',
    gap: 12,
    backgroundColor: `${theme.colors.neutral.primary[0]}`,
    borderRadius: 8,
  },
  wrapper: {
    position: 'absolute',
    display: 'flex',
    width: 48,
    height: 48,
    top: -24,
    left: 242,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${theme.colors.neutral.primary[0]}`,
    border: `4px solid ${theme.colors.neutral.primary[5]}`,
    borderRadius: '50%',
  },
  icon: {
    width: 24,
    height: 24,
  },
  title: {
    width: '100%',
    height: 32,
    padding: `${theme.layout.spacing.mikro} 0 ${theme.layout.spacing.nano}`,
    fontFamily: 'Roboto-Bold',
    fontSize: 12,
    textAlign: 'center',
  },
  description: {
    padding: `${theme.layout.spacing.deka} ${theme.layout.spacing.deka} ${theme.layout.spacing.centy}`,
  },
});

// const ICONS: Record<number, string> = {
//   1: SeekIcon,
//   2: EvaluateIcon,
//   3: DiscussIcon,
// };

interface ISuggestionsPage {
  prmCode: EPRMCode;
}

const SuggestionsPage: FC<ISuggestionsPage> = ({ prmCode }) => {
  const { t } = useTranslation();

  const hints = useMemo(
    () => [
      {
        id: 1,
        title: t(`results.report.suggestions.hints.${prmCode}.seek.title`),
        text: t(`results.report.suggestions.hints.${prmCode}.seek.description`),
      },
      {
        id: 2,
        title: t(`results.report.suggestions.hints.${prmCode}.evaluate.title`),
        text: t(`results.report.suggestions.hints.${prmCode}.evaluate.description`),
      },
      {
        id: 3,
        title: t(`results.report.suggestions.hints.${prmCode}.discuss.title`),
        text: t(`results.report.suggestions.hints.${prmCode}.discuss.description`),
      },
    ],
    [prmCode, t]
  );

  return (
    <Page id="suggestions" orientation="portrait" size="A4" style={styles.page} wrap={false}>
      <PageSection title={t('results.report.pdf.sections.suggestions')} isUnstyled>
        <Text style={styles.description}>{t('results.report.suggestions.description')}</Text>
        <View style={styles.container}>
          {hints.map(({ id, title, text }) => (
            <View style={styles.card} key={id}>
              <View style={styles.inner}>
                <View style={styles.wrapper}>
                  <Image style={styles.icon} src={SeekIcon} />
                </View>
                <Text style={styles.title}>{title}</Text>
                <Text>{text}</Text>
              </View>
            </View>
          ))}
        </View>
      </PageSection>
      <PageCount />
    </Page>
  );
};

export default SuggestionsPage;
